import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Layers, Upload, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMySets } from "@/lib/queries";
import { CardGrid, CardGridSkeleton } from "@/components/cards/CardGrid";
import { EmptyState, PageHeader, Pager } from "@/components/common/PageParts";
import { SetCard, CreateSetDialog } from "@/pages/Sets";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function MyCards() {
  const [q, setQ] = useState("");
  const [visibility, setVisibility] = useState("");
  const [page, setPage] = useState(1);
  const params = Object.fromEntries(Object.entries({ q, visibility, page, page_size: 30 }).filter(([, v]) => v));
  const { data, isLoading } = useQuery({ queryKey: ["me", "cards", params], queryFn: () => api.get("/me/cards", { params }).then((r) => r.data), placeholderData: keepPreviousData });
  return (
    <>
      <div className="mb-6 flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
          <input className="field pl-9" placeholder="Search my cards" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Search my cards" data-testid="dashboard-search-input" />
        </div>
        <select className="field w-auto capitalize" value={visibility} onChange={(e) => { setVisibility(e.target.value); setPage(1); }} aria-label="Filter by visibility" data-testid="dashboard-visibility-filter">
          <option value="">All visibilities</option>
          <option value="public">Public</option>
          <option value="unlisted">Unlisted</option>
          <option value="private">Private</option>
        </select>
      </div>
      <p className="mb-4 font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="dashboard-card-count">{data ? `${data.total} cards` : ""}</p>
      {isLoading ? <CardGridSkeleton /> : data.items.length ? (
        <><CardGrid cards={data.items} showOwner={false} testid="dashboard-cards-grid" /><Pager page={page} pageSize={30} total={data.total} onChange={setPage} testid="dashboard-pager" /></>
      ) : (
        <EmptyState icon={Layers} title="No cards here yet" testid="dashboard-cards-empty">
          <Link to="/upload" className="text-amber-300 hover:underline" data-testid="dashboard-empty-upload-link">Upload your first card</Link>
        </EmptyState>
      )}
    </>
  );
}

function MySets() {
  const { data: sets = [], isLoading } = useMySets();
  if (isLoading) return null;
  return (
    <>
      <div className="mb-6"><CreateSetDialog /></div>
      {sets.length ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-sets-grid">{sets.map((s) => <SetCard key={s.set_id} set={s} showOwner={false} />)}</div>
        : <EmptyState title="No sets yet" testid="dashboard-sets-empty">Sets are also created automatically from set codes during bulk upload.</EmptyState>}
    </>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  return (
    <div className="container-x py-12">
      <PageHeader eyebrow="My Workshop" title={`Welcome, ${user.display_name}`} testid="dashboard-header"
        description="Everything you've created — including private and unlisted work."
        actions={<>
          <Link to="/upload" className="btn-outline" data-testid="dashboard-upload-button"><Upload className="h-4 w-4" />Upload card</Link>
          <Link to="/upload/bulk" className="btn-gold" data-testid="dashboard-bulk-button"><Layers className="h-4 w-4" />Bulk upload</Link>
          <Link to="/import" className="btn-outline" data-testid="dashboard-import-button"><Layers className="h-4 w-4" />Import cards / set</Link>
        </>} />
      <Tabs defaultValue="cards">
        <TabsList className="mb-8 bg-[#121620]">
          <TabsTrigger value="cards" data-testid="dashboard-tab-cards">My cards</TabsTrigger>
          <TabsTrigger value="sets" data-testid="dashboard-tab-sets">My sets</TabsTrigger>
        </TabsList>
        <TabsContent value="cards"><MyCards /></TabsContent>
        <TabsContent value="sets"><MySets /></TabsContent>
      </Tabs>
      <p className="mt-12 text-sm text-slate-500">
        Manage your card backs in the <Link to="/card-backs" className="text-amber-300 hover:underline" data-testid="dashboard-card-backs-link">card back library</Link>.
      </p>
    </div>
  );
}
